/**
 * @fileoverview Component for displaying navigation tabs.
 * This component renders a horizontal navigation bar with tabs that can be clicked to navigate between different views.
 * Tabs can be disabled and styled differently based on their current state.
 *
 * @example
 * const tabs = [
 *   { name: 'Overview', href: '/overview', current: true },
 *   { name: 'Details', href: '/details' },
 *   { name: 'Settings', href: '/settings', disabled: true }
 * ];
 *
 * <Tabs tabs={tabs} className="border-b" />
 */

import { Link } from 'react-router-dom'

/**
 * @function classNames
 * @description Combines multiple class names into a single string.
 * @param {...string[]} classes - The class names to combine.
 * @returns {string} - A single string of class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @interface TabProps
 * @description Props for the Tabs component.
 * @property {string} className - The class name for the component.
 * @property {Tab[]} tabs - The tabs to display.
 */
interface TabProps extends Partial<HTMLDivElement> {
    tabs: {
        name: string
        href: string
        current?: boolean
        disabled?: boolean
    }[]
}

/**
 * @function Tabs
 * @description Component for displaying navigation tabs.
 * @param {TabProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the tabs.
 */
export default function Tabs({ tabs, className }: TabProps) {
    return (
        <nav className={`${className} h-10 items-center flex`} aria-label="Tabs">
            {tabs.map((tab) =>
                tab.disabled ? (
                    <span
                        key={tab.name}
                        className="text-gray-400 hover:text-gray-400 cursor-default px-3 py-2 text-sm font-medium"
                    >
                        {tab.name}
                    </span>
                ) : (
                    <Link
                        key={tab.name}
                        to={tab.href}
                        replace
                        className={classNames(
                            tab.current && 'bg-indigo-50 text-gray-900',
                            'text-gray-600 hover:text-gray-700 rounded-md px-3 py-2.5 text-sm font-medium'
                        )}
                        aria-current={tab.current ? 'page' : undefined}
                    >
                        <span>{tab.name}</span>
                    </Link>
                )
            )}
        </nav>
    )
}
